import { useState, useEffect, Fragment } from 'react'
import { useNavigate, type NavigateFunction } from 'react-router-dom'
import { Search, LayoutGrid, List, Columns, ChevronRight, ChevronDown, Printer } from 'lucide-react'
import { service } from '../lib/api/service'
import { entryToATA } from '../lib/api/transform'
import type { Project, Surcharge, ATAView, StatusHistory } from '../lib/api/types'
import { formatSEK } from '../utils/calculations'
import { statusStepBg, filterPipeline, type StatusStep } from '../utils/statusUtils'
import { useProjectConfig } from '../lib/appContext'
import KPICard from './KPICard'
import StatusBadge from './StatusBadge'
import MissingChips from './MissingChips'

const VIEWS = ['Hybrid', 'Tavla', 'Lista']

export default function Dashboard() {
  const navigate = useNavigate()
  const { hiddenStatuses, statuses } = useProjectConfig()
  const activePipeline = filterPipeline(hiddenStatuses, statuses)

  const [project, setProject]       = useState<Project | null>(null)
  const [atas, setAtas]             = useState<ATAView[]>([])
  const [, setSurcharges]           = useState<Surcharge[]>([])
  const [loading, setLoading]       = useState(true)
  const [view, setView]             = useState('Lista')
  const [search, setSearch]         = useState('')
  const [filterStatus, setFilterStatus] = useState('')

  useEffect(() => {
    Promise.all([service.getProject(), service.getAllEntries(), service.getSurcharges()])
      .then(([proj, entries, sc]) => {
        setProject(proj)
        setAtas(entries.map(e => entryToATA(e, sc)))
        setSurcharges(sc)
      })
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="loading"><div className="spinner" /></div>

  // ── KPI values ───────────────────────────────────────────────────────────
  const totalAntal    = atas.length
  const pagaende      = atas.filter(a => !['godkand','fakturerad'].includes(a.status)).length
  const avslutade     = totalAntal - pagaende
  const prissattVarde = atas.reduce((s, a) => s + (a.belopp ?? 0), 0)
  const godkantVarde  = atas.filter(a => ['godkand','fakturerad'].includes(a.status))
                            .reduce((s, a) => s + (a.belopp ?? 0), 0)
  const godkandaCount = atas.filter(a => a.status === 'godkand').length
  const fakturerade   = atas.filter(a => a.status === 'fakturerad').length
  const kontrakts     = project?.kontraktssumma ?? null
  const tillagg       = kontrakts && prissattVarde
    ? ((prissattVarde / kontrakts) * 100).toLocaleString('sv-SE', { maximumFractionDigits: 1 }) + ' %'
    : null

  const formatKort = (n: number | null) => {
    if (!n) return '–'
    if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toLocaleString('sv-SE', { maximumFractionDigits: 1 }) + ' mkr'
    if (Math.abs(n) >= 1_000)    return (n / 1_000).toLocaleString('sv-SE', { maximumFractionDigits: 0 }) + ' tkr'
    return n.toLocaleString('sv-SE') + ' kr'
  }

  // ── Att göra ─────────────────────────────────────────────────────────────
  const saknarU     = atas.filter(a => !a.harUnderrattelse)
  const saknarSpec  = atas.filter(a => !a.harSpec && a.status !== 'upplagd')
  const invanarSvar = atas.filter(a => a.bestallarSvar === 'Inväntar besked')
  const attGoraTotal = saknarU.length + saknarSpec.length + invanarSvar.length

  // ── Filtered list ─────────────────────────────────────────────────────────
  const filtered = atas.filter(a => {
    const q = search.toLowerCase()
    const matchSearch = !q ||
      String(a.sortOrder).includes(q) ||
      a.rubrik.toLowerCase().includes(q) ||
      a.beskrivning.toLowerCase().includes(q)
    const matchStatus = !filterStatus || a.status === filterStatus
    return matchSearch && matchStatus
  })

  const projectLine = project
    ? [project.projektnr, project.projektNamn, project.ort].filter(Boolean).join(' · ')
    : null

  return (
    <div>
      <div className="page-header">
        <div>
          {projectLine && <div className="page-eyebrow">{projectLine}</div>}
          <h1 className="page-title">Översikt</h1>
        </div>
        <div className="page-actions">
          <button className="icon-btn" onClick={() => navigate('/print/journal')}>
            <Printer size={14} />
            Skriv ut översikt
          </button>
        </div>
      </div>

      {/* Att göra */}
      <AttGora
        attGoraTotal={attGoraTotal}
        saknarU={saknarU}
        saknarSpec={saknarSpec}
        invanarSvar={invanarSvar}
        navigate={navigate}
      />

      {/* KPIs */}
      <div className="kpi-grid" style={{ marginBottom: 24 }}>
        <KPICard
          label="Totalt antal ÄTA"
          value={totalAntal.toLocaleString('sv-SE')}
          sub={`${pagaende} pågående · ${avslutade} avslutade`}
        />
        <KPICard
          label="Prissatt värde"
          value={formatKort(prissattVarde)}
          sub="Summa av alla prissatta ÄTA"
        />
        <KPICard
          label="Godkänt värde"
          value={formatKort(godkantVarde)}
          sub={`${godkandaCount} godkända · ${fakturerade} fakturerade`}
        />
        <KPICard
          label="Kontraktssumma"
          value={kontrakts ? formatKort(kontrakts) : '–'}
          sub={tillagg ? `Tillägg ${tillagg}` : 'Ej angiven'}
        />
      </div>

      {/* View toolbar */}
      <div className="view-toolbar">
        <span className="view-toolbar-title">Alla ÄTA</span>
        <span className="view-toolbar-count"
          style={{ borderLeft: '.5px solid var(--border)', paddingLeft: 10, marginLeft: 2 }}>
          {filtered.length !== atas.length
            ? `${filtered.length} av ${atas.length}`
            : atas.length.toLocaleString('sv-SE')}
        </span>
        <div className="view-toolbar-spacer" />

        {/* Search */}
        <div className="search-input">
          <Search size={13} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Sök ÄTA…"
          />
        </div>

        {/* Status filter */}
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="field-input"
          style={{ width: 'auto', padding: '5px 10px', fontSize: 12.5 }}
        >
          <option value="">Alla statusar</option>
          {activePipeline.map(s => (
            <option key={s.id} value={s.id}>{s.kort}</option>
          ))}
        </select>

        {/* View toggle */}
        <div className="view-toggle">
          {VIEWS.map(v => (
            <button
              key={v}
              className={`view-toggle-btn${view === v ? ' active' : ''}`}
              onClick={() => setView(v)}
            >
              {v === 'Hybrid' && <Columns size={13} />}
              {v === 'Tavla'  && <LayoutGrid size={13} />}
              {v === 'Lista'  && <List size={13} />}
              {v}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      {atas.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <List size={40} />
            <h3>Inga ÄTA registrerade</h3>
            <p>Klicka på "Ny ÄTA" för att skapa den första ändringsordran.</p>
          </div>
        </div>
      ) : (
        <>
          {(view === 'Hybrid' || view === 'Tavla') && (
            <BoardView atas={filtered} navigate={navigate} compact={view === 'Hybrid'} pipeline={activePipeline} />
          )}
          {(view === 'Hybrid' || view === 'Lista') && (
            <ListaView atas={filtered} navigate={navigate} />
          )}
        </>
      )}
    </div>
  )
}

// ── Att göra ─────────────────────────────────────────────────────────────────

interface AttGoraProps {
  attGoraTotal: number
  saknarU: ATAView[]
  saknarSpec: ATAView[]
  invanarSvar: ATAView[]
  navigate: NavigateFunction
}

function AttGora({ attGoraTotal, saknarU, saknarSpec, invanarSvar, navigate }: AttGoraProps) {
  const cols = [
    { label: 'Saknar underrättelse', items: saknarU, type: 'warn' },
    { label: 'Saknar specifikation', items: saknarSpec, type: 'warn' },
    { label: 'Inväntar svar från beställare', items: invanarSvar, type: 'info' },
  ]
  return (
    <div className="att-gora">
      <div className="att-gora-header">
        <div className={`att-gora-counter ${attGoraTotal > 0 ? 'has' : 'zero'}`}>
          {attGoraTotal > 0 ? attGoraTotal : '✓'}
        </div>
        <span className="att-gora-title">Att göra</span>
        <span className="att-gora-desc">
          {attGoraTotal === 0
            ? 'Allt är i ordning'
            : `${attGoraTotal} ärenden kräver uppmärksamhet`}
        </span>
      </div>
      <div className="att-gora-body">
        {cols.map(col => (
          <div key={col.label} className="att-gora-col">
            <div
              className={`att-gora-count ${col.items.length > 0 ? 'has' : 'zero'}`}
            >
              {col.items.length}
            </div>
            <div className="att-gora-col-label">{col.label}</div>
            {col.items.slice(0, 3).map(a => (
              <button
                key={a.sortOrder}
                className="att-gora-row"
                onClick={() => navigate(`/ata/${a.sortOrder}`)}
              >
                <span className="att-gora-nr">
                  ÄTA-{String(a.sortOrder).padStart(3, '0')}
                </span>
                <span className="att-gora-rubrik">{a.rubrik || '(ingen rubrik)'}</span>
                <ChevronRight size={13} className="chev-icon" />
              </button>
            ))}
            {col.items.length > 3 && (
              <div className="att-gora-overflow">
                +{col.items.length - 3} till
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Board view ────────────────────────────────────────────────────────────────

interface BoardViewProps {
  atas: ATAView[]
  navigate: NavigateFunction
  compact: boolean
  pipeline: StatusStep[]
}

function BoardView({ atas, navigate, compact, pipeline }: BoardViewProps) {
  return (
    <div className="board" style={{ marginBottom: compact ? 16 : 0, gridTemplateColumns: `repeat(${pipeline.length}, minmax(180px, 1fr))` }}>
      {pipeline.map(info => {
        const sid = info.id
        const cards = atas.filter(a => a.status === sid)
        return (
          <div key={sid} className="board-col">
            <div className="board-col-header">
              <span className="board-col-dot" style={{ background: statusStepBg(info) }} />
              <span className="board-col-label">{info.kort}</span>
              <span className="board-col-count">{cards.length}</span>
            </div>
            <div className="board-cards">
              {cards.length === 0 && <div className="board-empty">—</div>}
              {cards.map(a => (
                <div key={a.sortOrder} className="board-card" onClick={() => navigate(`/ata/${a.sortOrder}`)}>
                  <div className="board-card-top">
                    <span className="board-card-nr">ÄTA-{String(a.sortOrder).padStart(3,'0')}</span>
                    {a.ansvarig && (
                      <span className="board-card-avatar">{a.ansvarig.slice(0,2).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="board-card-title">{a.rubrik || '(ingen rubrik)'}</div>
                  {!compact && a.belopp != null && (
                    <div className="board-card-belopp">{formatSEK(a.belopp)}</div>
                  )}
                  {!compact && (
                    <MissingChips
                      saknarU={!a.harUnderrattelse}
                      saknarSpec={!a.harSpec && a.status !== 'upplagd'}
                      invanarSvar={a.bestallarSvar === 'Inväntar besked'}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Lista view ────────────────────────────────────────────────────────────────

const formatUpdated = (s: string | null) =>
  s ? new Date(s).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' }) : '–'

function ListaView({ atas, navigate }: { atas: ATAView[]; navigate: NavigateFunction }) {
  // Senaste status är expanderbar (WI #12359): klick laddar statushistoriken lat.
  const [expanded, setExpanded] = useState<number | null>(null)
  const [history, setHistory] = useState<Record<number, StatusHistory[] | 'loading'>>({})

  const toggle = async (sortOrder: number) => {
    if (expanded === sortOrder) { setExpanded(null); return }
    setExpanded(sortOrder)
    if (history[sortOrder] === undefined) {
      setHistory(prev => ({ ...prev, [sortOrder]: 'loading' }))
      try {
        const h = await service.getStatusHistory(sortOrder)
        setHistory(prev => ({ ...prev, [sortOrder]: h }))
      } catch {
        setHistory(prev => ({ ...prev, [sortOrder]: [] }))
      }
    }
  }

  return (
    <div className="table-card" style={{ marginTop: 8 }}>
      <table>
        <thead>
          <tr>
            <th style={{ width: 80 }}>Nr</th>
            <th>Rubrik</th>
            <th style={{ width: 150 }}>Status</th>
            <th style={{ width: 140 }}>Senast uppdaterad</th>
            <th className="num" style={{ width: 120 }}>Belopp</th>
            <th style={{ width: 160 }}>Saknas / svar</th>
            <th style={{ width: 38, textAlign: 'center' }}>U</th>
            <th style={{ width: 38, textAlign: 'center' }}>S</th>
          </tr>
        </thead>
        <tbody>
          {atas.length === 0 && (
            <tr><td colSpan={8} style={{ textAlign:'center', color:'var(--ink-faint)', padding:24 }}>
              Inga ÄTA matchar filtret
            </td></tr>
          )}
          {atas.map(a => {
            const isOpen = expanded === a.sortOrder
            const hist = history[a.sortOrder]
            return (
              <Fragment key={a.sortOrder}>
                <tr onClick={() => navigate(`/ata/${a.sortOrder}`)}>
                  <td className="mono">ÄTA-{String(a.sortOrder).padStart(3,'0')}</td>
                  <td style={{ fontWeight: 500 }}>{a.rubrik || <span style={{ color:'var(--ink-faint)' }}>–</span>}</td>
                  <td
                    onClick={e => { e.stopPropagation(); toggle(a.sortOrder) }}
                    style={{ cursor: 'pointer' }}
                    title="Visa statushistorik"
                  >
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                      <StatusBadge status={a.status} small />
                      {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    </span>
                  </td>
                  <td style={{ fontSize:12.5, color:'var(--ink-dim)' }}>{formatUpdated(a.senastUppdaterad)}</td>
                  <td className="num">{a.belopp != null ? formatSEK(a.belopp) : <span style={{ color:'var(--ink-faint)' }}>–</span>}</td>
                  <td>
                    <MissingChips
                      saknarU={!a.harUnderrattelse}
                      saknarSpec={!a.harSpec && a.status !== 'upplagd'}
                      invanarSvar={a.bestallarSvar === 'Inväntar besked'}
                    />
                  </td>
                  <td style={{ textAlign:'center' }} onClick={e => { e.stopPropagation(); navigate(`/ata/${a.sortOrder}?tab=1`) }}>
                    <IndicatorCell present={a.harUnderrattelse} letter="U" clickable />
                  </td>
                  <td style={{ textAlign:'center' }} onClick={e => { e.stopPropagation(); navigate(`/ata/${a.sortOrder}?tab=2`) }}>
                    <IndicatorCell present={a.harSpec} letter="S" clickable />
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={8} style={{ background:'var(--bg-2)', padding:'8px 14px 10px 54px' }}>
                      {hist === undefined || hist === 'loading' ? (
                        <span style={{ fontSize:12.5, color:'var(--ink-faint)' }}>Laddar statushistorik…</span>
                      ) : hist.length === 0 ? (
                        <span style={{ fontSize:12.5, color:'var(--ink-faint)' }}>Ingen statushistorik ännu.</span>
                      ) : (
                        <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                          {hist.map((h, i) => (
                            <div key={i} style={{ fontSize:12.5, display:'flex', gap:8, alignItems:'center' }}>
                              <span style={{ color:'var(--ink-faint)', fontFamily:'var(--mono)', minWidth:110 }}>
                                {formatUpdated(h.changedAt)}
                              </span>
                              {h.fromStatus && (
                                <>
                                  <StatusBadge status={h.fromStatus} small />
                                  <span style={{ color:'var(--ink-faint)' }}>→</span>
                                </>
                              )}
                              <StatusBadge status={h.toStatus} small />
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function IndicatorCell({ present, letter, clickable }: { present: boolean; letter: string; clickable?: boolean }) {
  const base = {
    display:'inline-flex', alignItems:'center', justifyContent:'center',
    width:22, height:18, borderRadius:4, fontSize:10, fontWeight:700,
    cursor: clickable ? 'pointer' : 'default',
  }
  if (present) {
    return (
      <span style={{ ...base, background:'oklch(0.94 0.04 145)', color:'oklch(0.36 0.12 145)' }} title={`Öppna ${letter === 'U' ? 'Underrättelse' : 'Specifikation'}`}>
        {letter}
      </span>
    )
  }
  return (
    <span style={{ ...base, fontWeight:500, border:'.5px dashed var(--border)', color:'var(--ink-faint)' }} title={`Öppna ${letter === 'U' ? 'Underrättelse' : 'Specifikation'}`}>
      {letter}
    </span>
  )
}
